/**
 * AnnouncedCountdown — a visible, announced, HOLDABLE countdown to something
 * the surface is about to do on the person's behalf.
 *
 * Story 26739. Two rules are load-bearing and both are enforced by tests:
 *
 * 1. A COUNTDOWN NEVER SUBMITS ANYTHING. `action.kind` is `"close" | "navigate"`
 *    and there is no third member. A timer that answers a question for somebody
 *    is not a convenience, it is an unattributable decision — and the whole
 *    decision surface exists so every answer has a person's name on it. The
 *    union is checked as SOURCE TEXT by `noTimerDrivenApproval.test.tsx`,
 *    because a `"submit"` member added here would type-check perfectly.
 *
 * 2. It can always be HELD. `onHold` cancels the countdown for good — there is
 *    no re-arm — and the button is reachable by keyboard before the timer
 *    expires, which is why the count is rendered as text rather than as a
 *    shrinking bar. The HOST can hold it too, through `held`, for the case the
 *    button cannot cover: the person is already busy in the surface the action
 *    would take away, and asking them to notice a countdown and press Hold
 *    first is asking them to watch the clock instead of doing the work.
 *
 * REDUCED MOTION. With `prefers-reduced-motion: reduce` the number does not
 * tick: the label renders once with the ORIGINAL count and the timer runs as a
 * single wait. The action still happens — this is an auto-close, not a
 * decoration — but nothing on screen animates, and the Hold button is
 * unchanged. Ticking a digit once a second is exactly the kind of repeated
 * motion the preference is about, and a live region that re-announced every
 * second would be worse still.
 *
 * ACCESSIBILITY. `announcement` is spoken ONCE, from a polite live region, at
 * the moment the countdown starts. The ticking digits are `aria-hidden`: a
 * screen reader reading "4… 3… 2…" over somebody trying to find the Hold
 * button is the failure this shape avoids.
 *
 * Shared: no Office.js, no Remix server import. The host does the actual
 * closing — this component only says it is coming and offers the way out.
 */
import { useEffect, useRef, useState } from "react";
import { usePrefersReducedMotion } from "~/common/hooks/usePrefersReducedMotion";

/**
 * What the countdown is going to do.
 *
 * `close`    — dismiss this surface (the task pane, the drawer).
 * `navigate` — take the person somewhere else.
 *
 * THERE IS NO `"submit"`. See rule 1 in the module docstring; adding one is a
 * product decision, not a refactor.
 */
export type CountdownActionKind = "close" | "navigate";

export interface CountdownAction {
  kind: CountdownActionKind;
  /**
   * The whole sentence, in ora's voice. The token `{seconds}` is replaced with
   * the live remaining count (or, under reduced motion, with the original
   * count). A label with no token renders verbatim.
   *
   * e.g. `"Back to your queue in {seconds}s — nothing is lost, ora will email you"`.
   */
  label: string;
}

export interface AnnouncedCountdownProps {
  /** How long to wait. Zero or less fires on the first frame after render. */
  seconds: number;
  action: CountdownAction;
  /** Called ONCE, after `0` has been rendered. Never called after a hold. */
  onFire?: () => void;
  /** Called when the person holds. The countdown does not re-arm. */
  onHold?: () => void;
  /**
   * Held from OUTSIDE — the host has decided this countdown must not run,
   * because the person started doing something the action would interrupt
   * (attaching a document to the run, say). Identical in every respect to
   * pressing Hold: the wait stops, nothing fires, and there is no re-arm while
   * it stays true.
   *
   * It is the HOST'S hold state, not a copy of it, so the row on screen and
   * whatever the host has scheduled underneath it cannot disagree about
   * whether the countdown is still running — the same reason rule 2 keeps the
   * Hold button and the timer on one flag.
   */
  held?: boolean;
  /**
   * What a screen reader hears once, when the countdown starts. Defaults to
   * the resolved label.
   */
  announcement?: string;
}

const HOLD_LABEL = "Hold";

function renderLabel(label: string, seconds: number): string {
  return label.replace(/\{seconds\}/g, String(Math.max(0, seconds)));
}

export function AnnouncedCountdown({
  seconds,
  action,
  onFire,
  onHold,
  announcement,
  held: heldByHost = false,
}: AnnouncedCountdownProps) {
  const reducedMotion = usePrefersReducedMotion();
  const [remaining, setRemaining] = useState(() => Math.max(0, seconds));
  const [heldHere, setHeldHere] = useState(false);
  // EITHER hold stops it and neither releases the other: the host's hold is not
  // the person's to undo from in here, and pressing Hold has to work whether or
  // not the host has already held it.
  const held = heldByHost || heldHere;
  const firedRef = useRef(false);
  const onFireRef = useRef(onFire);
  onFireRef.current = onFire;

  // Restart cleanly if the caller changes the budget (a different run in the
  // same mounted drawer). Without this the pane inherits the previous run's
  // remaining count.
  useEffect(() => {
    firedRef.current = false;
    setHeldHere(false);
    setRemaining(Math.max(0, seconds));
  }, [seconds]);

  // The wait. Under reduced motion it is ONE timeout for the whole budget, so
  // nothing on screen changes until it is over.
  useEffect(() => {
    if (held || remaining <= 0) return;
    const step = reducedMotion ? remaining : 1;
    const timer = window.setTimeout(() => {
      setRemaining((r) => Math.max(0, r - step));
    }, step * 1000);
    return () => window.clearTimeout(timer);
  }, [held, remaining, reducedMotion]);

  // Fire AFTER `0` has been rendered — a countdown that jumps from 1 to gone
  // reads as a glitch, and the last frame is the one that proves the wait
  // actually elapsed.
  useEffect(() => {
    if (held || remaining > 0 || firedRef.current) return;
    firedRef.current = true;
    onFireRef.current?.();
  }, [held, remaining]);

  const shown = reducedMotion ? Math.max(0, seconds) : remaining;
  const text = renderLabel(action.label, shown);

  if (held) {
    return (
      <div
        className="flex items-center gap-2 rounded-md border border-primary-gray-light-200 bg-primary-gray-light-25 px-3 py-2"
        data-testid="announced-countdown"
        data-countdown-state="held"
        data-countdown-kind={action.kind}
      >
        <p className="text-xs text-primary-gray-light-700">
          Held — this stays open until you close it.
        </p>
      </div>
    );
  }

  return (
    <div
      className="flex items-center justify-between gap-2 rounded-md border border-primary-gray-light-200 bg-primary-gray-light-25 px-3 py-2"
      data-testid="announced-countdown"
      data-countdown-state="running"
      data-countdown-kind={action.kind}
    >
      {/* Spoken once, when the countdown starts. Deliberately NOT the ticking
          text — see the module docstring. */}
      <span className="sr-only" role="status" aria-live="polite">
        {announcement ?? renderLabel(action.label, Math.max(0, seconds))}
      </span>
      <p
        className="text-xs text-primary-gray-light-700"
        aria-hidden="true"
        data-testid="announced-countdown-label"
      >
        {text}
      </p>
      <button
        type="button"
        onClick={() => {
          setHeldHere(true);
          onHold?.();
        }}
        className="shrink-0 rounded-md border border-primary-gray-light-300 bg-base-white px-2 py-1 text-2xs font-semibold text-primary-gray-light-700 hover:bg-primary-gray-light-100"
        data-testid="announced-countdown-hold"
      >
        {HOLD_LABEL}
      </button>
    </div>
  );
}

export default AnnouncedCountdown;
